import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import _noop from 'lodash/noop';
import { Documentation } from 'graphql-common';
import useDocumentsMap from '@lib/utils/fetch/hooks/useDocumentsMap';

type RegisterFn = (key: string, docIds: string[]) => void;
type UnregisterFn = (key: string) => void;

type FormDocumentsMapContextValue = {
  documentsMap: Map<string, Documentation>;
  loading: boolean;
  registerDocIds: RegisterFn;
  unregisterDocIds: UnregisterFn;
  hasProvider: boolean;
};

const FormDocumentsMapContext = createContext<FormDocumentsMapContextValue>({
  documentsMap: new Map(),
  loading: false,
  registerDocIds: _noop,
  unregisterDocIds: _noop,
  hasProvider: false,
});

/**
 * Aggregates docIds reported by all DOCUMENT-kind question children into a
 * single `useEdmDocumentationsLightQuery` call so the form fires one network
 * request instead of one per question (editor + preview each duplicated).
 */
export function FormDocumentsMapProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [registry, setRegistry] = useState<Record<string, string[]>>({});

  const allDocIds = useMemo(() => {
    const set = new Set<string>();
    Object.values(registry).forEach((ids) => ids.forEach((id) => set.add(id)));
    return [...set].sort();
  }, [registry]);

  const { documentsMap, loading } = useDocumentsMap(allDocIds);

  const registerDocIds = useCallback<RegisterFn>((key, docIds) => {
    setRegistry((prev) => {
      const prevForKey = prev[key];
      if (
        prevForKey &&
        prevForKey.length === docIds.length &&
        prevForKey.every((id, i) => id === docIds[i])
      ) {
        return prev;
      }
      return { ...prev, [key]: docIds };
    });
  }, []);

  const unregisterDocIds = useCallback<UnregisterFn>((key) => {
    setRegistry((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo<FormDocumentsMapContextValue>(
    () => ({
      documentsMap,
      loading,
      registerDocIds,
      unregisterDocIds,
      hasProvider: true,
    }),
    [documentsMap, loading, registerDocIds, unregisterDocIds],
  );

  return (
    <FormDocumentsMapContext.Provider value={value}>
      {children}
    </FormDocumentsMapContext.Provider>
  );
}

/**
 * Returns a shared documents map keyed by id. When called inside
 * `<FormDocumentsMapProvider>`, it registers `docIds` with the provider so the
 * whole form fires a single `edmDocumentations` query. When called outside
 * (e.g., view/task routes that reuse the same components without the
 * provider), it falls back to its own `useDocumentsMap(docIds)` so the caller
 * still gets data.
 */
export default function useFormDocumentsMap(docIds: string[]) {
  const ctx = useContext(FormDocumentsMapContext);
  const id = useId();

  const sortedDocIds = useMemo(() => [...docIds].sort(), [docIds]);
  const docIdsKey = sortedDocIds.join(',');

  // Always called to keep hook order stable. When a provider is present we
  // pass an empty array so it short-circuits via `skip` inside the hook.
  const fallback = useDocumentsMap(ctx.hasProvider ? [] : sortedDocIds);

  useEffect(() => {
    if (!ctx.hasProvider) return undefined;
    if (sortedDocIds.length === 0) {
      ctx.unregisterDocIds(id);
      return undefined;
    }
    ctx.registerDocIds(id, sortedDocIds);
    return () => ctx.unregisterDocIds(id);
    // sortedDocIds is captured via docIdsKey identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    docIdsKey,
    id,
    ctx.hasProvider,
    ctx.registerDocIds,
    ctx.unregisterDocIds,
  ]);

  if (ctx.hasProvider) {
    return { documentsMap: ctx.documentsMap, loading: ctx.loading };
  }
  return fallback;
}
